import { call, takeEvery } from 'redux-saga/effects';

import { put } from 'typed-redux-saga';
import * as actions from './executive.action';
import {
	fetchDepartmentRatio,
	fetchIrrRatio,
	fetchRcrRatio,
	fetchVocRatio,
} from 'services/dashboardRevamp/executiveDashboard/sidebar.service';

import {
	HeatmapIssueFlagsI,
	IrrRatioI,
	RcrRatioI,
	VocRationI,
	IssueListI,
	ReportListI,
	HealthScoreDataI,
} from './executive.types';
import { fetchHeatmapIssueFlags } from 'services/dashboardRevamp/executiveDashboard/header.service';
import {
	fetchIssueList,
	fetchReportList,
	fetchExecutiveHealth,
} from 'services/dashboardRevamp/executiveDashboard/perfomanceCard.service';

function* fetchDepartmentRatioSaga(action: ReturnType<typeof actions.fetchDepartmentRatioAsync.request>): Generator {
	try {
		const res = (yield call(fetchDepartmentRatio) as unknown) as string;
		yield put(actions.fetchDepartmentRatioAsync.success(res));
	} catch {
		yield put(actions.fetchDepartmentRatioAsync.failure('Failed to get Report details'));
	}
}

function* fetchIrrRatioSaga(action: ReturnType<typeof actions.fetchIrrRatioAsync.request>): Generator {
	try {
		const res = (yield call(fetchIrrRatio) as unknown) as IrrRatioI;
		yield put(actions.fetchIrrRatioAsync.success(res));
	} catch {
		yield put(actions.fetchIrrRatioAsync.failure('Failed to get Report details'));
	}
}

function* fetchRcrRatioSaga(action: ReturnType<typeof actions.fetchRcrRatioAsync.request>): Generator {
	try {
		const res = (yield call(fetchRcrRatio) as unknown) as RcrRatioI;
		yield put(actions.fetchRcrRatioAsync.success(res));
	} catch {
		yield put(actions.fetchRcrRatioAsync.failure('Failed to get Report details'));
	}
}

function* fetchVocRatioSaga(action: ReturnType<typeof actions.fetchVocRatioAsync.request>): Generator {
	try {
		const res = (yield call(fetchVocRatio) as unknown) as VocRationI;
		yield put(actions.fetchVocRatioAsync.success(res));
	} catch {
		yield put(actions.fetchVocRatioAsync.failure('Failed to get Report details'));
	}
}

function* fetchHeatmapIssueFlagsSaga(
	action: ReturnType<typeof actions.fetchHeatmapIssueFlagsAsync.request>,
): Generator {
	try {
		const res = (yield call(fetchHeatmapIssueFlags) as unknown) as HeatmapIssueFlagsI;
		yield put(actions.fetchHeatmapIssueFlagsAsync.success(res));
	} catch {
		yield put(actions.fetchHeatmapIssueFlagsAsync.failure('Failed to get heatmap issue flag details'));
	}
}

// PerfomanceCard Issue List Sagas
function* fetchPerformanceIssuesBySiteSaga(
	action: ReturnType<typeof actions.fetchPerformanceIssuesBySiteAsync.request>,
): Generator {
	try {
		const { viewBy, sortBy, sortDirection, page } = action.payload;
		const res = (yield call(fetchIssueList, {
			viewBy,
			groupBy: 'site',
			sortBy,
			sortDirection,
			page,
		}) as unknown) as IssueListI;
		yield put(actions.fetchPerformanceIssuesBySiteAsync.success(res));
	} catch {
		yield put(actions.fetchPerformanceIssuesBySiteAsync.failure('Failed to get Performance Issues by Site'));
	}
}

function* fetchPerformanceIssuesByUserSaga(
	action: ReturnType<typeof actions.fetchPerformanceIssuesByUserAsync.request>,
): Generator {
	try {
		const { viewBy, sortBy, sortDirection, page } = action.payload;
		const res = (yield call(fetchIssueList, {
			viewBy,
			groupBy: 'user',
			sortBy,
			sortDirection,
			page,
		}) as unknown) as IssueListI;
		yield put(actions.fetchPerformanceIssuesByUserAsync.success(res));
	} catch {
		yield put(actions.fetchPerformanceIssuesByUserAsync.failure('Failed to get Performance Issues by User'));
	}
}

function* fetchPerformanceIssuesByDepartmentSaga(
	action: ReturnType<typeof actions.fetchPerformanceIssuesByDepartmentAsync.request>,
): Generator {
	try {
		const { viewBy, sortBy, sortDirection, page } = action.payload;
		const res = (yield call(fetchIssueList, {
			viewBy,
			groupBy: 'department',
			sortBy,
			sortDirection,
			page,
		}) as unknown) as IssueListI;
		yield put(actions.fetchPerformanceIssuesByDepartmentAsync.success(res));
	} catch {
		yield put(
			actions.fetchPerformanceIssuesByDepartmentAsync.failure('Failed to get Performance Issues by Department'),
		);
	}
}

function* fetchPerformanceIssuesByCategorySaga(
	action: ReturnType<typeof actions.fetchPerformanceIssuesByCategoryAsync.request>,
): Generator {
	try {
		const { viewBy, sortBy, sortDirection, page } = action.payload;
		const res = (yield call(fetchIssueList, {
			viewBy,
			groupBy: 'category',
			sortBy,
			sortDirection,
			page,
		}) as unknown) as IssueListI;
		yield put(actions.fetchPerformanceIssuesByCategoryAsync.success(res));
	} catch {
		yield put(actions.fetchPerformanceIssuesByCategoryAsync.failure('Failed to get Performance Issues by Category'));
	}
}

// PerfomanceCard Report List Sagas
function* fetchPerformanceReportsBySiteSaga(
	action: ReturnType<typeof actions.fetchPerformanceReportsBySiteAsync.request>,
): Generator {
	try {
		const { viewBy, sortBy, sortDirection, page } = action.payload;
		const res = (yield call(fetchReportList, {
			viewBy,
			groupBy: 'site',
			sortBy,
			sortDirection,
			page,
		}) as unknown) as ReportListI[];
		yield put(actions.fetchPerformanceReportsBySiteAsync.success(res));
	} catch {
		yield put(actions.fetchPerformanceReportsBySiteAsync.failure('Failed to get Performance Reports by Site'));
	}
}

function* fetchPerformanceReportsByUserSaga(
	action: ReturnType<typeof actions.fetchPerformanceReportsByUserAsync.request>,
): Generator {
	try {
		const { viewBy, sortBy, sortDirection, page } = action.payload;
		const res = (yield call(fetchReportList, {
			viewBy,
			groupBy: 'user',
			sortBy,
			sortDirection,
			page,
		}) as unknown) as ReportListI[];
		yield put(actions.fetchPerformanceReportsByUserAsync.success(res));
	} catch {
		yield put(actions.fetchPerformanceReportsByUserAsync.failure('Failed to get Performance Reports by User'));
	}
}

function* fetchPerformanceReportsByDepartmentSaga(
	action: ReturnType<typeof actions.fetchPerformanceReportsByDepartmentAsync.request>,
): Generator {
	try {
		const { viewBy, sortBy, sortDirection, page } = action.payload;
		const res = (yield call(fetchReportList, {
			viewBy,
			groupBy: 'department',
			sortBy,
			sortDirection,
			page,
		}) as unknown) as ReportListI[];
		yield put(actions.fetchPerformanceReportsByDepartmentAsync.success(res));
	} catch {
		yield put(
			actions.fetchPerformanceReportsByDepartmentAsync.failure('Failed to get Performance Reports by Department'),
		);
	}
}

// HealthScore Sagas
function* fetchHealthScoreBySiteSaga(
	action: ReturnType<typeof actions.fetchHealthScoreBySiteAsync.request>,
): Generator {
	try {
		const res = (yield call(fetchExecutiveHealth, 'site') as unknown) as HealthScoreDataI;
		yield put(actions.fetchHealthScoreBySiteAsync.success(res));
	} catch {
		yield put(actions.fetchHealthScoreBySiteAsync.failure('Failed to get Health Score by Site'));
	}
}

function* fetchHealthScoreByUserSaga(
	action: ReturnType<typeof actions.fetchHealthScoreByUserAsync.request>,
): Generator {
	try {
		const res = (yield call(fetchExecutiveHealth, 'user') as unknown) as HealthScoreDataI;
		yield put(actions.fetchHealthScoreByUserAsync.success(res));
	} catch {
		yield put(actions.fetchHealthScoreByUserAsync.failure('Failed to get Health Score by User'));
	}
}

function* fetchHealthScoreByDepartmentSaga(
	action: ReturnType<typeof actions.fetchHealthScoreByDepartmentAsync.request>,
): Generator {
	try {
		const res = (yield call(fetchExecutiveHealth, 'department') as unknown) as HealthScoreDataI;
		yield put(actions.fetchHealthScoreByDepartmentAsync.success(res));
	} catch {
		yield put(actions.fetchHealthScoreByDepartmentAsync.failure('Failed to get Health Score by Department'));
	}
}

function* fetchHealthScoreByCategorySaga(
	action: ReturnType<typeof actions.fetchHealthScoreByCategoryAsync.request>,
): Generator {
	try {
		const res = (yield call(fetchExecutiveHealth, 'category') as unknown) as HealthScoreDataI;
		yield put(actions.fetchHealthScoreByCategoryAsync.success(res));
	} catch {
		yield put(actions.fetchHealthScoreByCategoryAsync.failure('Failed to get Health Score by Category'));
	}
}

export default function* executiveDashboardSaga() {
	// Existing watchers
	yield takeEvery(actions.fetchDepartmentRatioAsync.request, fetchDepartmentRatioSaga);
	yield takeEvery(actions.fetchIrrRatioAsync.request, fetchIrrRatioSaga);
	yield takeEvery(actions.fetchRcrRatioAsync.request, fetchRcrRatioSaga);
	yield takeEvery(actions.fetchVocRatioAsync.request, fetchVocRatioSaga);
	yield takeEvery(actions.fetchHeatmapIssueFlagsAsync.request, fetchHeatmapIssueFlagsSaga);

	// PerfomanceCard Issue List watchers
	yield takeEvery(actions.fetchPerformanceIssuesBySiteAsync.request, fetchPerformanceIssuesBySiteSaga);
	yield takeEvery(actions.fetchPerformanceIssuesByUserAsync.request, fetchPerformanceIssuesByUserSaga);
	yield takeEvery(actions.fetchPerformanceIssuesByDepartmentAsync.request, fetchPerformanceIssuesByDepartmentSaga);
	yield takeEvery(actions.fetchPerformanceIssuesByCategoryAsync.request, fetchPerformanceIssuesByCategorySaga);

	// PerfomanceCard Report List watchers
	yield takeEvery(actions.fetchPerformanceReportsBySiteAsync.request, fetchPerformanceReportsBySiteSaga);
	yield takeEvery(actions.fetchPerformanceReportsByUserAsync.request, fetchPerformanceReportsByUserSaga);
	yield takeEvery(actions.fetchPerformanceReportsByDepartmentAsync.request, fetchPerformanceReportsByDepartmentSaga);

	// HealthScore watchers
	yield takeEvery(actions.fetchHealthScoreBySiteAsync.request, fetchHealthScoreBySiteSaga);
	yield takeEvery(actions.fetchHealthScoreByUserAsync.request, fetchHealthScoreByUserSaga);
	yield takeEvery(actions.fetchHealthScoreByDepartmentAsync.request, fetchHealthScoreByDepartmentSaga);
	yield takeEvery(actions.fetchHealthScoreByCategoryAsync.request, fetchHealthScoreByCategorySaga);
}
